//Svelte methods
import { get } from 'svelte/store';
//Spa methods
import { push, replace } from 'svelte-spa-router';
//Helpers
import { get_mode, is_gen_2, isDistance, moduleTransformId } from 'src/helpers/interface_helpers';
import { compare, validate } from 'compare-versions';
import { getTemplateDeviceInfo, isXT1Module } from './xt1_helpers/template_helpers';
import { getXT1Services } from 'src/helpers/xt1_helpers/template_extract_helpers';
//Configs
import { devices_config } from '@devices/index';
//Services
import { postReq } from 'src/services/http';
import {
	disconnect_from_device_ios,
	stop_bluetooth_search_ios,
} from 'src/services/native_container_requests_ios';
import {
	disconnect_from_device_android,
	stop_bluetooth_search_android,
} from 'src/services/native_container_requests_android';
//Stores
import { user_data } from 'src/stores/account/user';
import {
	discovered_statuses,
	discovered_settings,
	discovered_info,
} from 'src/stores/device-runtime/status';
import { devices } from 'src/stores/lists/devices';
import { os, app, ios_version, android_version } from 'src/stores/session/native-bridge';
import { device_object } from 'src/stores/wizards';
import { addDeviceModal } from 'src/stores/device-runtime/firmware-ui';
import {
	includeDeviceModal,
	includingDeviceData,
	inclusionStarted,
	inclusionState,
} from 'src/stores/features/inclusion';
//Types
import type { Device } from 'src/types/Device';
import type { DeviceInfoG2 } from 'src/types/DeviceInfoG2';
import type { NativeInclusionData } from 'src/types/NativeInclusionData';
import type { IsOwnedRequest } from 'src/types/responses/DeviceOwned';
import { saved_wifi_neworks } from 'src/stores/features/wifi/passwords';
import { validate_email } from './validators';
import { getBLUDeviceMode } from './web_bluetooth/web_bluetooth_inclusion_helpers';
//Constants
import { setSidePanelState } from 'src/components/Sidepanel/sidepanel-helpers';
import {
	BLE_BUTTONS_WITH_4_INPUTS,
	BLE_DEVICES_ZB,
	SLEEPING_TYPES,
	WALL_DISPLAYS_WITHOUT_INTERNAL_SENSOR,
} from 'src/constants';
import { BLU_DEVICES_WITH_ONE_CLICK_PAIRING } from 'src/configs/web_bluetooth_exclude_devices_filter';
import { format } from 'svelte-i18n';
import { capFirstChar } from './utilities';
import { DISTANCE_ADDITIONAL_PROPS } from './distance_helper';
import { bthomeDeviceToModel } from './virtual_helpers/virtual_bthome_helper';

export interface XTDeviceTransformResult {
	image?: string;
	name?: string;
}

/**
 * Applies XT device ID transformation (jti) and resolves XT1 module metadata.
 * Mutates `device` in place (id, jti, service_type, category).
 * Returns image/name so callers can apply them to local UI state.
 */
export const applyXTDeviceTransform = (
	device: {
		id: string;
		type: string;
		jti?: string;
		service_type?: string;
		category?: string;
	},
	info: DeviceInfoG2,
): XTDeviceTransformResult | undefined => {
	if (!info) return undefined;

	const result: XTDeviceTransformResult = {};

	if (info.jti) {
		device.id = moduleTransformId(device.id, info.jti);
		device.jti = info.jti;
	}

	if (isXT1Module(device.type)) {
		const templateConfig = getTemplateDeviceInfo(info);
		result.image = templateConfig.image;
		result.name = templateConfig.name;
		device.service_type = templateConfig.type;
		device.category = templateConfig.category;
	}

	return result;
};

export interface BuildDiscoveredDeviceParams {
	device: {
		id: string;
		type: string;
		gen: string;
		ip?: string;
		jti?: string;
		service_type?: string;
		category?: string;
	};
	image: string;
	config: any;
	status: any;
	settings: any;
	mode?: string;
}

/** Builds a Device object from discovered device data, config, status/settings, and resolved mode. */
export const buildDiscoveredDevice = (params: BuildDiscoveredDeviceParams): Device => {
	const { device, image, config, status, settings, mode } = params;
	const xt1Services = isXT1Module(device.type) ? getXT1Services(config) : [];
	const channelsCount =
		xt1Services.length > 0 ? xt1Services.length : config?.modes?.[mode]?.channels || 1;

	const _device: Device = {
		id: device.id,
		type: device.type,
		category: mode === 'roller' || mode === 'cover' ? 'roller' : config?.family,
		position: -1,
		gen: device.gen,
		channel: 0,
		channels_count: channelsCount,
		mode: '',
		name: undefined,
		room_id: undefined,
		image,
		cloud_options: { exclude_event_log: false },
		bundle: typeof status?.ble?.dev_list !== 'undefined',
		ip: device.ip,
		ss: SLEEPING_TYPES.includes(device.type) ? { status, settings } : undefined,
	};

	if (device.jti) _device.jti = device.jti;

	if (isXT1Module(device.type) && device.service_type) {
		_device.service_type = device.service_type;
		_device.category = device.category;
	}

	return _device;
};

/** Resets device_object store, sets the built device with cloud enablement flag, and opens the add-device modal. */
export const submitDeviceForInclusion = (deviceData: Device, settings: any): void => {
	console.log('%c Will pass device data to association Modal: ', 'color:green', deviceData);

	device_object.reset();
	device_object.set({
		...deviceData,
		enableCloudAfterAccountAssociation: !settings?.cloud?.enabled || !settings?.cloud?.enable,
	});

	addDeviceModal.set(true);
};

export const isOwned = async (id) => {
	const res: IsOwnedRequest = await postReq('/device/is_available', {
		id,
	});

	return res?.errors?.device_exists;
};

export const translate_single_device_container_data = async (
	scanData: NativeInclusionData,
): Promise<Device> => {
	if (!scanData) return;

	let type =
		typeof scanData.type !== 'undefined'
			? scanData.type
			: typeof scanData.model !== 'undefined'
				? scanData.model
				: typeof scanData?.info?.type !== 'undefined'
					? scanData.info.type
					: typeof scanData?.device?.type !== 'undefined'
						? scanData.device.type
						: typeof scanData?.info?.model !== 'undefined'
							? scanData.info.model
							: typeof scanData?.device?.model !== 'undefined'
								? scanData.device.model
								: scanData?.settings?.wifi?.ap?.ssid?.split('-')[0];

	if (typeof type !== 'undefined' && typeof devices_config[type] === 'undefined') {
		Object.entries(devices_config).map(([i, e]) => {
			if (e.name.toLowerCase() === type.toLowerCase()) {
				type = i;
			}
		});
	}

	if (typeof devices_config[type] === 'undefined') {
		// console.error('Trying to add a device with unknown type:', type);
		return {};
	}

	const gen =
		typeof scanData?.gen !== 'undefined'
			? scanData?.gen
			: typeof scanData?.info?.gen !== 'undefined'
				? scanData?.info?.gen
				: 1;

	let id = is_gen_2({ gen })
		? scanData?.info?.id.split('-').pop() // .pop() will take the last matched string after split, which is not dependent of the ID format
		: scanData?.longid == 1 && typeof scanData?.mac !== 'undefined'
			? scanData.mac
			: scanData?.longid && typeof scanData?.id !== 'undefined'
				? scanData.id
				: scanData?.info?.longid == 1
					? scanData?.info.mac
					: scanData?.info?.mac.substring(6);

	if (gen === 1 || is_gen_2({ gen })) id = id.toLowerCase();

	// Devices with jti have a different ID format and need to be transformed
	if (scanData?.info?.jti) {
		id = moduleTransformId(id, scanData?.info?.jti);
	}

	// TODO: Add to device object the jti
	const device: Device = {
		id,
		type,
		category: devices_config[type].family,
		position: -1,
		gen: gen + '',
		channel: 0,
		channels_count: 1,
		mode: scanData?.settings?.sys?.device?.profile || '',
		name: undefined,
		room_id: undefined,
		image: `images/device_images/${type}.png`,
		cloud_options: { exclude_event_log: false },
		...(scanData?.info?.jti && { jti: scanData.info.jti }), // include jti only if it exists
	};

	includingDeviceData.updateData({ ...device });

	const info = scanData.info || {};

	discovered_info.update((info_list) => {
		return { ...info_list, [id]: info };
	});

	const status = scanData.status || {};

	discovered_statuses.update((status_list) => {
		return { ...status_list, [id]: status };
	});

	const settings = scanData.settings || {};

	discovered_settings.update((settings_list) => {
		return { ...settings_list, [id]: settings };
	});

	const mode = !device.mode ? get_mode(device, status, settings) : device.mode;

	console.log('%cMode derived from scanData:', 'color:orange', mode, 'this sure needs a fix...');

	device.channels_count = devices_config[type]?.modes[mode]?.channels || 1;

	device.category = ['roller', 'cover'].includes(mode) ? 'roller' : devices_config[type]?.family;

	device.mode = mode;

	device.ss = SLEEPING_TYPES.includes(type) ? { status, settings } : undefined;

	if (isXT1Module(type)) {
		//@ts-ignore
		const config = getTemplateDeviceInfo(scanData?.info);
		device.service_type = config.type;
		device.image = config.image;
		device.category = config.category;
	}

	const tempStatus = status?.['temperature:0'];
	if (
		WALL_DISPLAYS_WITHOUT_INTERNAL_SENSOR.includes(type) &&
		((!tempStatus?.tC && !tempStatus?.tF) || (tempStatus?.tC < 0 && tempStatus?.tF < 0))
	) {
		device.hideCharts = true;
	}

	console.log('Will store device data to pass to from util to the process route', device);

	if (typeof status?.ble?.dev_list !== 'undefined') {
		device.bundle = true;
	}

	device_object.reset();
	device_object.set(device);
	// use this if you want to open TemplatesModal from inclusion process
	//saveSettingsToDevice(device, settings);
	// save_data_for_process_handle(device, mode);
	return device;
};

export const translateSingleDeviceDataFromNativeIAmFeelingLucky = (scanData: NativeInclusionData) => {
	// no data passed
	if (!scanData) return false;
	console.log('%cscanData passed:', 'color:green', scanData);

	let type =
		typeof scanData.type !== 'undefined'
			? scanData.type
			: typeof scanData.model !== 'undefined'
				? scanData.model
				: typeof scanData?.info?.type !== 'undefined'
					? scanData.info.type
					: typeof scanData?.device?.type !== 'undefined'
						? scanData.device.type
						: typeof scanData?.info?.model !== 'undefined'
							? scanData.info.model
							: typeof scanData?.device?.model !== 'undefined'
								? scanData.device.model
								: scanData?.settings?.wifi?.ap?.ssid?.split('-')[0];

	if (typeof type !== 'undefined' && typeof devices_config[type] === 'undefined') {
		Object.entries(devices_config).map(([i, e]) => {
			if (e.name.toLowerCase() === type.toLowerCase()) {
				type = i;
			}
		});
	}

	console.log('%cType derived from scanData:', 'color:red', type);

	if (typeof devices_config[type] === 'undefined') {
		console.error('Trying to add a device with unknown type:', type);
		return false;
	}

	const id =
		scanData.gen == '2'
			? scanData.id
			: scanData.device?.longid
				? scanData.device.mac
				: scanData.device.mac.substring(6, 12);

	console.log('%cID derived from scanData:', 'color:yellow', id);

	const device: Device = {
		id: id,
		type,
		category: devices_config[type].family,
		position: -1,
		gen: scanData.gen,
		channel: 0,
		channels_count: 1,
		mode: scanData?.settings?.sys?.device?.profile || '',
		name: undefined,
		room_id: undefined,
		image: `images/device_images/${type}.png`,
		cloud_options: { exclude_event_log: false },
	};

	includingDeviceData.updateData({ ...device });

	const info = scanData.info || {};
	discovered_info.update((info_list) => {
		return { ...info_list, [id]: info };
	});

	const status = scanData.status || {};
	discovered_statuses.update((status_list) => {
		return { ...status_list, [id]: status };
	});

	const settings = scanData.settings || {};
	discovered_settings.update((settings_list) => {
		return { ...settings_list, [id]: settings };
	});

	const mode = !device.mode ? get_mode(device, status, settings) : device.mode;

	console.log('%cMode derived from scanData:', 'color:orange', mode);

	device.channels_count = devices_config[type]?.modes?.[mode]?.channels || 1;

	device.category = mode === 'roller' || mode === 'cover' ? 'roller' : devices_config[type]?.family;
	device.mode = mode;

	console.log('%category derived from scanData:', 'color:purple', device.category);

	console.log('%cWill pass store in device_object: ', 'color: green, font-size:25px', device);

	if (typeof status?.ble?.dev_list !== 'undefined') {
		device.bundle = true;
	}

	device_object.reset();
	device_object.set(device);
	// use this if you want to open TemplatesModal from inclusion process
	//saveSettingsToDevice(device, settings);
	// save_data_for_process_handle(device, mode);
	return true;
};

export const translate_single_device_container_data_gble = (scanData: NativeInclusionData) => {
	if (!scanData) return;

	let type =
		scanData.type !== undefined && devices_config[scanData.type] !== undefined
			? scanData.type
			: scanData.model !== undefined && devices_config[scanData.model] !== undefined
				? scanData.model
				: scanData.info?.code !== undefined
					? scanData.info.code
					: undefined;

	if (typeof devices_config[type] === 'undefined') {
		Object.entries(devices_config).map(([i, e]) => {
			if (e.name.toLowerCase() === type.toLowerCase()) {
				type = i;
			}
		});
	}
	// console.warn('type from gble', type);
	if (typeof devices_config[type] === 'undefined') {
		console.error('Trying to add a device with unknown type:', type);
		return false;
	}

	const gen = 'GBLE';

	const id = scanData?.id || scanData?.info?.id || scanData?.info?.xb;
	console.warn('id from translate single device data gble', id);

	const key_enc = scanData?.settings?.encryptionKey || '';
	const buzzer_enabled = scanData?.settings?.buzzerEnabled;
	const beacon_enabled = scanData?.settings?.beaconEnabled;

	let device: Device = {
		id,
		type,
		category: devices_config[type].family,
		position: -1,
		gen,
		channel: 0,
		channels_count: 1,
		mode: '',
		name: undefined,
		room_id: undefined,
		image: `images/device_images/${type}.png`,
		cloud_options: { exclude_event_log: false },
		key_enc,
		beacon_enabled,
		buzzer_enabled,
	};
	// includingDeviceData.updateData({ ...device });
	const info = scanData.info || {};
	discovered_info.update((info_list) => {
		return { ...info_list, [id]: info };
	});

	// const status = scanData.status || {};

	// discovered_statuses.update(status_list => {
	// 	return { ...status_list, [id]: status };
	// });

	// console.warn('device from include ble', device);

	const settings = scanData.settings || {};

	discovered_settings.update((settings_list) => {
		return { ...settings_list, [id]: settings };
	});

	const mode = devices_config[type]?.family || getBLUDeviceMode(type);

	device.channels_count = devices_config[type]?.modes?.[mode]?.channels || 1;

	device.category = devices_config[type]?.family;
	device.mode = mode;

	if (isDistance(type)) device = { ...device, ...DISTANCE_ADDITIONAL_PROPS };

	console.log('Will store device data to pass to from util to the process route', device);

	device_object.reset();
	device_object.set(device);
	// save_data_for_process_handle(device, mode);
	return true;
};

export const isSupportedVersion = (customMinVersion = undefined) => {
	const isApp = get(app);
	const isIOS = get(os) === 'IOS';
	const minVersion = isIOS ? '1.0.3' : '1.13.0';
	const version = isIOS ? get(ios_version) : get(android_version);
	if (isApp) {
		if (customMinVersion) {
			if (!validate(customMinVersion)) {
				console.error('unsupported customMinVersion!');
				return false;
			} else {
				return compare(version, customMinVersion, '>=');
			}
		}

		if (!validate(minVersion) || !validate(version)) return false;
		return compare(version, minVersion, '>=');
	}
};
/**
 * @function routeToDevice is used to open drawer during inclusion process if device is already included in client account
 */
export const routeToDevice = (device) => {
	replace('/dash');
	push(`/home/room/${get(devices)[device.id].room_id}/devices`);
	setSidePanelState({
		type: 'device',
		id: device.id,
		open: true,
	});
};
/**
 * @function closeInclusionModal Closing inclusion modal cleaning inclusion device object and state
 */
export const closeInclusionModal = () => {
	includeDeviceModal.update((state) => {
		return (state = { ...state, show: false });
	});
	inclusionStarted.set(false);
	includingDeviceData.reset();
	inclusionState.set({});
};
export const stopBluetoothScanner = () => {
	if (get(os) === 'IOS') {
		disconnect_from_device_ios();
		stop_bluetooth_search_ios();
	} else {
		disconnect_from_device_android();
		stop_bluetooth_search_android();
	}
};

export const handleOldWiFiData = () => {
	let _saved_wifi_neworks = get(saved_wifi_neworks);
	if (_saved_wifi_neworks !== null && Object.keys(_saved_wifi_neworks).length > 0) {
		// Iterate over $saved_wifi_neworks and check if every key is a valid email.
		// If it is not a valid email, create a new key with the current user_data.email.
		// This new key will be used to store ssid and pass.
		// The ssid value will be the invalid email key and pass will be the value of this key.
		Object.keys(_saved_wifi_neworks).forEach((key) => {
			if (!validate_email(key)) {
				const emailKey = get(user_data).email;
				saved_wifi_neworks.update(emailKey, {
					ssid: key,
					pass: _saved_wifi_neworks[key],
				});
				saved_wifi_neworks.remove(key);
			}
		});
	}
};

export const pairingMessageWrapper = (
	deviceType: string | undefined,
	currentFirmware?: string,
	isWeb?: boolean,
) => {
	let message = get(format)('alert_message_ble_pair_description', {
		default: 'Please press and hold the device button for 10 seconds.',
	});

	/** find device model by type to get hex value */
	let deviceModel;
	let hexValue = null;

	if (deviceType) {
		/** extract base model type (remove suffix like -77d6 from SBBT-004CUS-77d6) */
		/** model types are typically 2 parts (1 dash): SBBT-004CUS */
		/** if deviceType has more parts, extract the base model type */
		const parts = deviceType.split('-');
		const baseModelType = parts.length > 2 ? parts.slice(0, 2).join('-') : deviceType;
		deviceModel = bthomeDeviceToModel.find((model) =>
			[baseModelType, deviceType].includes(model.type),
		);
		hexValue = deviceModel ? parseInt(deviceModel.hex, 16) : null;
	}
	/** determine pairing method based on hex model ID */
	if (hexValue !== null) {
		if (hexValue < 0x0006) {
			/** legacy devices: hold button for 10 seconds */
			message = isWeb
				? get(format)('alert_message_ble_pair_required', {
						default:
							'Please press and hold the device button for 10 seconds and click Next button.',
					})
				: get(format)('alert_message_ble_pair_description', {
						default: 'Please press and hold the device button for 10 seconds.',
					});
		} else if (hexValue <= 0x0007) {
			/** devices with hex 0x0006 or 0x0007: hold 2 buttons for 10 seconds */
			message = get(format)('alert_message_buttons_ble_pair_description', {
				default: 'Please press and hold any two buttons on the device for 10 seconds.',
			});
		} else {
			/** newer devices (hex > 0x0007): press button 4 times */
			message = isWeb
				? get(format)('alert_message_ble_pair_required_for_zb', {
						default:
							'Please press any button on the device 4 times in a row and click Next button.',
					})
				: get(format)('alert_message_ble_pair_description_for_zb', {
						default:
							'Please press any button on the device 4 times in a row to start pairing.',
					});
		}
	} else if (deviceType && BLE_DEVICES_ZB.includes(deviceType)) {
		message = isWeb
			? get(format)('alert_message_ble_pair_required_for_zb', {
					default:
						'Please press any button on the device 4 times in a row and click Next button.',
				})
			: get(format)('alert_message_ble_pair_description_for_zb', {
					default: 'Please press any button on the device 4 times in a row to start pairing.',
				});
	} else if (
		deviceType &&
		currentFirmware &&
		compare(currentFirmware, '1.0.21', '>=') &&
		BLE_BUTTONS_WITH_4_INPUTS.includes(deviceType)
	) {
		message = get(format)('alert_message_buttons_ble_pair_description', {
			default: 'Please press and hold any two buttons on the device for 10 seconds.',
		});
	} else if (deviceType && BLE_BUTTONS_WITH_4_INPUTS.includes(deviceType)) {
		message = get(format)('alert_message_buttons_ble_pair_required', {
			default:
				'If your device firmware is version 1.0.21 or higher, press and hold any two buttons for 10 seconds. If it’s lower than 1.0.21, press and hold at least one button for 10 seconds. Then click the Next button.',
		});
	} else if (deviceType && BLU_DEVICES_WITH_ONE_CLICK_PAIRING.test(deviceType)) {
		message = isWeb
			? get(format)('alert_message_ble_pair_init', {
					default:
						'If you try to include BLE or a sleeping device, please press the device button before continuing.',
				})
			: get(format)('press_button_required', {
					default: 'Please press device button.',
				});
	} else {
		message = isWeb
			? get(format)('alert_message_ble_pair_required', {
					default:
						'Please press and hold the device button for 10 seconds and click Next button.',
				})
			: get(format)('alert_message_ble_pair_description', {
					default: 'Please press and hold the device button for 10 seconds.',
				});
	}

	return capFirstChar(message);
};
